// import { exposeObject } from "./pages";

function exposeObject(key: string, value: any) {
    (window as any)[key] = value
}

const _evalCounter = (() => {
    let c = 0;
    return () => { return c++; };
})();

const EVAL_RESULT_CONTAINER_PREFIX = "__envrc_";

export async function scriptEval(code: string, name: string|null = null) {
    const key = `${EVAL_RESULT_CONTAINER_PREFIX}${_evalCounter()}`

    const script = document.createElement("script");
    script.nonce = __webpack_nonce__;
    script.id = key;
    
    try {
        return await new Promise((rs, rj) => {
            exposeObject(key, {rs, rj});

            script.onerror = () => rj(new Error(`Failed to load script ${name || '<unnamed>'}. More at 11`));
            const text = document.createTextNode(`try { window.${key}.rs((() => {${code}})()) } catch (e) { window.${key}.rj(e); }`);
            script.appendChild(text);

            document.head.appendChild(script);
        });
    } finally {
        delete (window as any)[key];
    }
}

export async function scriptExec(code: string, name: string|null = null) {
    const key = `${EVAL_RESULT_CONTAINER_PREFIX}${_evalCounter()}`

    const script = document.createElement("script");
    script.nonce = __webpack_nonce__;
    script.id = key;
    
    try {
        await new Promise((rs, rj) => {
            exposeObject(key, {rs, rj});

            script.onerror = () => rj(new Error(`Failed to load script ${name || '<unnamed>'}. More at 11`));
            const text = document.createTextNode(`try { ${code} } catch (e) { window.${key}.rj(e); } window.${key}.rs();`);
            script.appendChild(text);

            document.head.appendChild(script);
        });
    } finally {
        delete (window as any)[key];
    }
}


export async function scriptRequire(url: string) {
    const key = `${EVAL_RESULT_CONTAINER_PREFIX}${_evalCounter()}`

    await new Promise<void>((rs, rj) => {
        const script = document.createElement("script");
        script.nonce = __webpack_nonce__;
        script.id = key;
        script.src = url;
        script.onerror = () => rj(new Error(`Failed to load script ${url}`));
        script.onload = () => rs();
        document.head.appendChild(script);
    });
}


/**
 * Inserts arbitrary HTML into the page and executes any <script> tags it contains.
 * Intended for use INSIDE your sandboxed iframe (separate subdomain).
 * Automatically applies Webpack's CSP nonce via __webpack_nonce__ when needed.
 */
export async function htmlUnsafeExecute(target: HTMLElement, content: string) {
    // Parse into a detached container
    const wrapper = document.createElement('div');
    wrapper.innerHTML = content;

    // Append first so relative URLs & base URIs resolve as expected
    target.appendChild(wrapper);

    // Gather scripts in document order
    const scripts = Array.from(wrapper.querySelectorAll('script')) as HTMLScriptElement[];

    // Webpack nonce, if present
    const webpackNonce = (window as any).__webpack_nonce__ as string | undefined;

    const errors = [];

    for (const oldScript of scripts) {
        const fresh = document.createElement('script');

        // Copy all attributes (src, type, async, defer, nonce, integrity, crossorigin, etc.)
        for (const { name, value } of Array.from(oldScript.attributes)) {
            fresh.setAttribute(name, value);
        }

        // If no nonce present and Webpack provided one, set it
        if (webpackNonce && !fresh.hasAttribute('nonce')) {
            fresh.setAttribute('nonce', webpackNonce);
        }

        // Inline script code
        if (!fresh.src) {
            fresh.text = oldScript.textContent ?? '';
        }

        // Preserve sequencing for classic external scripts without async/defer
        const isModule = (fresh.type || '').toLowerCase() === 'module';
        const isAsync = fresh.hasAttribute('async');
        const isDefer = fresh.hasAttribute('defer');
        const shouldAwait = !!fresh.src && !isModule && !isAsync && !isDefer;

        if (shouldAwait) {
            oldScript.replaceWith(fresh);
            try {
                await new Promise<void>((resolve, reject) => {
                    fresh.addEventListener('load', () => resolve(), { once: true });
                    fresh.addEventListener('error', () => reject(new Error(`Failed to load ${fresh.src}`)), { once: true });
                });
            } catch (e) {
                errors.push(Promise.reject(e));
            }
        } else if (!!fresh.text && !isAsync && !isDefer) {
            const key = `__evrc_${_evalCounter()}`;
            try {
                await new Promise<void>((rs, rj) => {
                    (window as any)[key] = {rs, rj}
                    fresh.text = `try {\n${fresh.text}} catch (e) {window.${key}.rj(e);} window.${key}.rs();`
                    fresh.addEventListener('error', () => rj(new Error(`Failed to load inline script.`)), { once: true });
                    oldScript.replaceWith(fresh);
                });
            } catch (e) {
                errors.push(Promise.reject(e));
            } finally {
                delete (window as any)[key];
            }
        } else {
            oldScript.replaceWith(fresh);
        }

        if (errors.length === 1) await errors[0]
        else if (errors.length > 1) await Promise.all(errors);
    }

    // Optional: remove the wrapper but keep its children
    while (wrapper.firstChild) target.insertBefore(wrapper.firstChild, wrapper);
    wrapper.remove();
}
